import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import logging from "../utils/logging";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { UserPlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AssignDialog({ open, onClose, alertId, onAssigned }) {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [assigning, setAssigning] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users/list`, { withCredentials: true });
      setUsers(res.data);
    } catch (err) {
      logging.error("Failed to fetch users", err);
    }
  }, []);

  useEffect(() => {
    if (open) { fetchUsers(); setSelectedUser(""); }
  }, [open, fetchUsers]);

  const handleAssign = async () => {
    if (!selectedUser) { toast.error(t("assign.select_required")); return; }
    setAssigning(true);
    try {
      await axios.patch(`${API}/alerts/${alertId}/assign`, { user_id: selectedUser }, { withCredentials: true });
      toast.success(t("assign.assigned_toast"));
      onAssigned();
    } catch {
      toast.error(t("assign.failed"));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm rounded-sm" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg font-bold flex items-center gap-2">
            <UserPlus className="w-5 h-5" /> {t("assign.title")}
          </DialogTitle>
        </DialogHeader>
        <Select value={selectedUser} onValueChange={setSelectedUser}>
          <SelectTrigger className="rounded-sm border-gray-300 font-body text-sm" data-testid="assign-user-select">
            <SelectValue placeholder={t("assign.placeholder")} />
          </SelectTrigger>
          <SelectContent className="rounded-sm">
            {users.map((u) => (
              <SelectItem key={u.user_id} value={u.user_id} className="font-body text-sm">
                {u.name} ({u.role})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm font-body">{t("common.cancel")}</Button>
          <Button onClick={handleAssign} disabled={assigning || !selectedUser} className="bg-black hover:bg-gray-800 text-white rounded-sm font-body" data-testid="confirm-assign-btn">
            {t("assign.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
